// Admin control for the app-wide force-update gate (the `config/app` doc). Flip
// it on and set a minimum build/version; native players below it get a
// non-dismissible "Update now" wall the next time they open (or, since the read
// is live, within seconds if they're already open). Web is never gated.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { ForceUpdateConfig } from '@h2h/shared';
import { Switch } from '../../components/ui/Switch';
import { useAppConfig } from '../../hooks/useQueries';
import { setAppConfig, isWrongPasswordError } from '../../lib/adminApi';
import { APP_STORE_URL } from '../../lib/appStore';
import { cn } from '../../lib/cn';
import { Panel, Pill, useAdminData } from './parts';

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

function Field({
  label,
  hint,
  children
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-[12px] font-semibold text-ink2">{label}</span>
      {hint && <span className="mt-0.5 block text-[11px] text-ink3">{hint}</span>}
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

const inputClass =
  'w-full rounded-xl border border-black/10 bg-wash px-3 py-2.5 text-[14px] text-ink outline-none transition-colors focus:border-accent/60';

export default function ForceUpdatePanel() {
  const { password } = useAdminData();
  const { data: appConfig } = useAppConfig();

  const [enabled, setEnabled] = useState(false);
  const [minBuild, setMinBuild] = useState('');
  const [minVersion, setMinVersion] = useState('');
  const [latestVersion, setLatestVersion] = useState('');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [iosUrl, setIosUrl] = useState('');

  const [state, setState] = useState<SaveState>('idle');
  const [error, setError] = useState<string | null>(null);

  // Seed the form from the stored doc once it first loads (undefined = still
  // loading). Don't re-seed afterwards so it never clobbers in-progress edits.
  const hydrated = useRef(false);
  useEffect(() => {
    if (hydrated.current || appConfig === undefined) return;
    const f = appConfig?.forceUpdate;
    setEnabled(!!f?.enabled);
    setMinBuild(f?.minBuild != null ? String(f.minBuild) : '');
    setMinVersion(f?.minVersion ?? '');
    setLatestVersion(f?.latestVersion ?? '');
    setTitle(f?.title ?? '');
    setMessage(f?.message ?? '');
    setIosUrl(f?.iosUrl ?? '');
    hydrated.current = true;
  }, [appConfig]);

  const stored = appConfig?.forceUpdate;
  const noThreshold = !minBuild.trim() && !minVersion.trim();
  const blocked = enabled && noThreshold;

  async function save() {
    if (state === 'saving' || blocked) return;
    setState('saving');
    setError(null);
    try {
      const cfg: ForceUpdateConfig = {
        enabled,
        minBuild: minBuild.trim() ? Number(minBuild) : undefined,
        minVersion: minVersion.trim() || undefined,
        latestVersion: latestVersion.trim() || undefined,
        title: title.trim() || undefined,
        message: message.trim() || undefined,
        iosUrl: iosUrl.trim() || undefined
      };
      await setAppConfig(password, cfg);
      setState('saved');
      setTimeout(() => setState((s) => (s === 'saved' ? 'idle' : s)), 2500);
    } catch (e) {
      setState('error');
      setError(
        isWrongPasswordError(e)
          ? 'Admin password rejected (it may have rotated). Lock and sign in again.'
          : (e as { message?: string })?.message || 'Could not save. Check the setAppConfig logs.'
      );
    }
  }

  const statusPill = stored?.enabled ? (
    <Pill tone="danger">Force update ON</Pill>
  ) : (
    <Pill>Off</Pill>
  );

  return (
    <Panel title="Force update" action={statusPill} bodyClassName="p-4 md:p-5">
      <p className="text-[13px] leading-[1.5] text-ink2">
        When on, anyone on the iOS app below the floor below gets a blocking
        “Update now” screen until they update. The web app is never gated.
      </p>
      {stored?.enabled && (
        <p className="mt-2 text-[12px] font-semibold text-danger">
          Live now — blocking players below
          {stored.minBuild != null ? ` build ${stored.minBuild}` : ''}
          {stored.minBuild != null && stored.minVersion ? ' /' : ''}
          {stored.minVersion ? ` v${stored.minVersion}` : ''}.
        </p>
      )}

      <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-black/10 bg-wash px-3.5 py-2.5">
        <div>
          <div className="text-[14px] font-bold text-ink">Force players to update</div>
          <div className="text-[12px] text-ink3">The master switch.</div>
        </div>
        <Switch checked={enabled} onChange={setEnabled} label="Force players to update" />
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <Field
          label="Minimum build"
          hint="iOS CFBundleVersion. The precise lever — recommended."
        >
          <input
            className={inputClass}
            inputMode="numeric"
            placeholder="e.g. 2"
            value={minBuild}
            onChange={(e) => setMinBuild(e.target.value.replace(/[^0-9]/g, ''))}
          />
        </Field>
        <Field label="Minimum version" hint="Marketing version, e.g. 1.2.0. Optional.">
          <input
            className={inputClass}
            placeholder="e.g. 1.2.0"
            value={minVersion}
            onChange={(e) => setMinVersion(e.target.value)}
          />
        </Field>
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <Field label="Latest version label" hint="Shown to the player. Optional.">
          <input
            className={inputClass}
            placeholder="e.g. 1.2.0"
            value={latestVersion}
            onChange={(e) => setLatestVersion(e.target.value)}
          />
        </Field>
        <Field label="App Store URL" hint="Defaults to the live listing.">
          <input
            className={inputClass}
            placeholder={APP_STORE_URL}
            value={iosUrl}
            onChange={(e) => setIosUrl(e.target.value)}
          />
        </Field>
      </div>

      <div className="mt-4 grid gap-4">
        <Field label="Title" hint="Modal heading. Optional.">
          <input
            className={inputClass}
            placeholder="Update required"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </Field>
        <Field label="Message" hint="Modal body. Optional.">
          <textarea
            className={cn(inputClass, 'min-h-[68px] resize-y')}
            placeholder="You’re on an older version of HeadToHead. Update to keep playing."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </Field>
      </div>

      {blocked && (
        <p className="mt-3 text-[12px] font-semibold text-danger">
          Set a minimum build or version before turning the gate on.
        </p>
      )}
      {state === 'error' && error && (
        <p className="mt-3 text-[12px] font-semibold text-danger">{error}</p>
      )}

      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={state === 'saving' || blocked}
          className={cn(
            'tap rounded-2xl bg-night px-5 py-3 text-[14px] font-bold text-white',
            (state === 'saving' || blocked) && 'opacity-50'
          )}
        >
          {state === 'saving' ? 'Saving…' : 'Save'}
        </button>
        {state === 'saved' && (
          <span className="text-[13px] font-semibold text-accent-ink">Saved</span>
        )}
      </div>
    </Panel>
  );
}
